/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

const POSITIVE_INTEGER_DEFINITION = '#/definitions/positiveInteger';
const POSITIVE_INTEGER_DEFAULT_DEFINITION = '#/definitions/positiveIntegerDefault0';
const SCHEMA_ARRAY = '#/definitions/schemaArray';

export const WIOTP_IM_SCHEMA_CONTENT = {
  id: 'http://json-schema.org/draft-04/schema#',
  $schema: 'http://json-schema.org/draft-04/schema#',
  description: 'Core schema meta-schema',
  definitions: {
    schemaArray: {
      type: 'array',
      minItems: 1,
      items: { $ref: '#' },
    },
    positiveInteger: {
      type: 'integer',
      minimum: 0,
    },
    positiveIntegerDefault0: {
      allOf: [{ $ref: POSITIVE_INTEGER_DEFINITION }, { default: 0 }],
    },
    simpleTypes: {
      enum: ['array', 'boolean', 'integer', 'null', 'number', 'object', 'string'],
    },
    stringArray: {
      type: 'array',
      items: { type: 'string' },
      minItems: 1,
      uniqueItems: true,
    },
  },
  type: 'object',
  properties: {
    id: {
      type: 'string',
    },
    $schema: {
      type: 'string',
    },
    title: {
      type: 'string',
    },
    description: {
      type: 'string',
    },
    default: {},
    multipleOf: {
      type: 'number',
      minimum: 0,
      exclusiveMinimum: true,
    },
    maximum: {
      type: 'number',
    },
    exclusiveMaximum: {
      type: 'boolean',
      default: false,
    },
    minimum: {
      type: 'number',
    },
    exclusiveMinimum: {
      type: 'boolean',
      default: false,
    },
    maxLength: { $ref: POSITIVE_INTEGER_DEFINITION },
    minLength: { $ref: POSITIVE_INTEGER_DEFAULT_DEFINITION },
    pattern: {
      type: 'string',
      format: 'regex',
    },
    additionalItems: {
      anyOf: [{ type: 'boolean' }, { $ref: '#' }],
      default: {},
    },
    items: {
      anyOf: [{ $ref: '#' }, { $ref: SCHEMA_ARRAY }],
      default: {},
    },
    maxItems: { $ref: POSITIVE_INTEGER_DEFINITION },
    minItems: { $ref: POSITIVE_INTEGER_DEFAULT_DEFINITION },
    uniqueItems: {
      type: 'boolean',
      default: false,
    },
    maxProperties: { $ref: POSITIVE_INTEGER_DEFINITION },
    minProperties: { $ref: POSITIVE_INTEGER_DEFAULT_DEFINITION },
    required: { $ref: '#/definitions/stringArray' },
    additionalProperties: {
      anyOf: [{ type: 'boolean' }, { $ref: '#' }],
      default: {},
    },
    definitions: {
      type: 'object',
      additionalProperties: { $ref: '#' },
      default: {},
    },
    properties: {
      type: 'object',
      additionalProperties: { $ref: '#' },
      default: {},
    },
    patternProperties: {
      type: 'object',
      additionalProperties: { $ref: '#' },
      default: {},
    },
    dependencies: {
      type: 'object',
      additionalProperties: {
        anyOf: [{ $ref: '#' }, { $ref: '#/definitions/stringArray' }],
      },
    },
    enum: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
    },
    type: {
      anyOf: [
        { $ref: '#/definitions/simpleTypes' },
        {
          type: 'array',
          items: { $ref: '#/definitions/simpleTypes' },
          minItems: 1,
          uniqueItems: true,
        },
      ],
    },
    format: { type: 'string' },
    allOf: { $ref: SCHEMA_ARRAY },
    anyOf: { $ref: SCHEMA_ARRAY },
    oneOf: { $ref: SCHEMA_ARRAY },
    not: { $ref: '#' },
  },
  dependencies: {
    exclusiveMaximum: ['maximum'],
    exclusiveMinimum: ['minimum'],
  },
  default: {},
};

export const WIOTP_IM_LI_RESOURCE = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      pattern: '^(?![ ]+$)[\\w \\-_.]+$',
      maxLength: 128,
      description: 'A human-readable name for the Logical Interface',
    },
    description: {
      type: 'string',
      maxLength: 1024,
      description: 'A human-readable description for the Logical Interface',
    },
    alias: {
      type: 'string',
      minLength: 1,
      maxLength: 36,
      pattern: '^[A-Za-z0-9\\-_.]+$',
      description:
        'An alias that can be used to reference the Logical Interface in state fetch requests',
    },
  },
  required: ['name'],
};

export const WIOTP_IM_MAPPING_RESOURCE = {
  type: 'object',
  properties: {
    notificationStrategy: {
      enum: ['on-every-event', 'on-state-change', 'never'],
      description: `The strategy used to determine when state change notifications should be published.
A notification strategy of never will disable state change notifications for the Logical Interface on a specific Device Type.
A notification strategy of on-state-change will only cause state change notifications to be published if the processing of an inbound event results in an actual change to one or more property values defined on the Logical Interface.
A notification strategy of on-every-event will cause state change notifications to be published every time an event is processed, even if it does not result in a change to a property value defined on the Logical Interface.`,
    },
    propertyMappings: {
      type: 'object',
      additionalProperties: {
        type: 'object',
        description: `The set of property mappings for a specific event.
The key is the event id that has been defined on the physical interface that is associated with the device type.`,
        additionalProperties: {
          type: 'string',
          description: `The definition of a single property mapping.
The key is the name of a property defined on the logical interface.
The value is an expression that is used to calculate the value of the property.
The expression could simply reference the value of a property on the inbound event, e.g., $event.property1 or it could define a calculation to be performed, e.g., **$event.property1 * 1.2.`,
        },
      },
    },
    logicalInterfaceId: {
      type: 'string',
      description: 'The ID of the logical interface ',
    },
  },
  required: ['notificationStrategy', 'propertyMappings', 'logicalInterfaceId'],
};

export const WIOTP_DEVICE_INFO_METADATA = {
  type: 'object',
  properties: {
    deviceInfo: {
      type: 'object',
      properties: {
        description: { type: 'string' },
        descriptiveLocation: {
          type: 'string',
          description:
            'A descriptive location, such as a room or building number, or a geographical region',
        },
        serialNumber: { type: 'string' },
        deviceClass: { type: 'string' },
        fwVersion: { type: 'string' },
        hwVersion: { type: 'string' },
        manufacturer: { type: 'string' },
        model: { type: 'string' },
      },
      additionalProperties: false,
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
};

export default {
  WIOTP_IM_LI_RESOURCE,
  WIOTP_IM_MAPPING_RESOURCE,
  WIOTP_IM_SCHEMA_CONTENT,
  WIOTP_DEVICE_INFO_METADATA,
};
